import React, { useState } from "react"
import { Body, H4, Small } from "../ui/typography"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Button } from "../ui/button"
import { CalendarRange } from "lucide-react"
import { cn } from "@/lib/utils"
import { ControllerRenderProps, UseFormReturn, useWatch } from "react-hook-form"
import { FormField, FormMessage } from "../ui/form"
import { PeriodFormDataOptionalRange } from "@/state/draft/period"
import { PlainDate } from "@/utils/date"
import { RangeCalendarWrapper } from "../primitives/RangeCalendarWrapper"
import { calculateReportFee, monthsBetween } from "@/utils/report"
import { usd2 } from "@/utils/formaters"

type PeriodSelectorProps = {
	form: UseFormReturn<PeriodFormDataOptionalRange>
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({ form }) => {
	const currentYear = new Date().getFullYear()
	const availableYears = [currentYear - 2, currentYear - 1]

	const values = useWatch({
		control: form.control,
		defaultValue: form.getValues(),
	}) as PeriodFormDataOptionalRange

	const dateRange = values.range.map((x) => (x ? PlainDate.from(x) : undefined))

	const [year, setYear] = useState<number>(
		() => dateRange?.[0]?.year() || availableYears[0]
	)

	const handleYearSelect = (selectedYear: number) => {
		setYear(selectedYear)
		form.setValue("isCustomDate", false)
		const startDate = `${selectedYear}-01-01`
		const endDate = `${selectedYear}-12-31`

		form.setValue("range", [startDate, endDate])
		form.clearErrors("range")
		setFee(calculateReportFee(12))
	}

	const handleCustomDateClick = () => {
		form.setValue("isCustomDate", true)
		form.setValue("range", [undefined, undefined])
	}

	const dateInputText =
		dateRange[0] && dateRange[1]
			? PlainDate.formatDateRange(dateRange[0], dateRange[1])
			: "Select date range"

	const monthsDiff = monthsBetween(dateRange[0], dateRange[1])
	const [fee, setFee] = useState<number | null>(() => calculateReportFee(monthsDiff))

	return (
		<div className="flex flex-col">
			<div className="flex flex-col gap-3.5">
				<H4>Select the period for your report</H4>
				<div className="flex gap-2">
					{availableYears.map((yearOption) => (
						<PeriodButton
							key={yearOption}
							onClick={() => handleYearSelect(yearOption)}
							isActive={
								!form.getValues().isCustomDate && year === yearOption
							}
						>
							{yearOption}
						</PeriodButton>
					))}
					<PeriodButton
						onClick={handleCustomDateClick}
						isActive={form.getValues().isCustomDate}
					>
						Custom Dates
					</PeriodButton>
				</div>
				<div className="max-w-[350px]">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-full justify-start text-left font-normal",
									!dateRange[0] && "text-muted-foreground"
								)}
								onClick={() => {
									if (!form.getValues().isCustomDate) {
										handleCustomDateClick()
									}
								}}
							>
								<Body>{dateInputText}</Body>
								<CalendarRange className="ml-auto h-4 w-4 stroke-muted-foreground" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<FormField
								control={form.control}
								name="range"
								rules={{
									validate: validateDateRange,
								}}
								render={({ field }) => (
									<Calendar field={field} form={form} setFee={setFee} />
								)}
							/>
						</PopoverContent>
					</Popover>
				</div>
			</div>
			{!!form.formState.errors.range && (
				<FormMessage>{form.formState.errors.range.message}</FormMessage>
			)}

			{monthsDiff > 0 && monthsDiff <= 12 && !!fee && (
				<Small className="text-muted-foreground font-normal mt-1.5">
					You've selected a {monthsDiff} month period. The report fee is{" "}
					{usd2(fee)}.
				</Small>
			)}
		</div>
	)
}

interface PeriodButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	isActive?: boolean
	children: React.ReactNode
}

type CalendarProps = {
	field: ControllerRenderProps<PeriodFormDataOptionalRange, "range">
	form: UseFormReturn<PeriodFormDataOptionalRange>
	setFee: React.Dispatch<React.SetStateAction<number | null>>
}

const option =
	<T extends any>(value: T | undefined) =>
	<Result extends any>(f: (x: T) => Result): Result | undefined =>
		typeof value == "undefined" ? undefined : f(value)

const Calendar: React.FC<CalendarProps> = ({ field, form, setFee }) => {
	function onSelect(range?: { from?: PlainDate; to?: PlainDate }) {
		const from = range?.from
		const to = range?.to

		field.onChange([from?.date, to?.date])

		form.setValue("range", [from?.date, to?.date], {
			shouldValidate: true,
		})
		if (from && to) {
			const monthsCount = monthsBetween(from, to)
			setFee(calculateReportFee(monthsCount))
		}
	}
	return (
		<RangeCalendarWrapper
			value={{
				from: option(field.value[0])((x) => PlainDate.from(x)),
				to: option(field.value[1])((x) => PlainDate.from(x)),
			}}
			onChange={onSelect}
		/>
	)
}

function validateDateRange(value: [string | undefined, string | undefined] | undefined) {
	if (!value || !value[0] || !value[1]) {
		return "Please select a date range"
	}

	const monthsCount = monthsBetween(PlainDate.from(value[0]), PlainDate.from(value[1]))

	if (monthsCount < 1 || monthsCount > 12) {
		return monthsCount < 1
			? "Period is too short. Please select at least 1 month"
			: "Period is too long. Please select no more than 1 year"
	}
	return true
}

function PeriodButton({ isActive, children, ...props }: PeriodButtonProps) {
	return (
		<Button
			variant="ghost"
			size={"sm"}
			className={cn(
				isActive
					? "outline outline-2 outline-offset-0 outline-primary"
					: "outline outline-1 outline-offset-0 outline-input",
				props.className
			)}
			{...props}
		>
			{children}
		</Button>
	)
}
